import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Grid, Tab } from 'semantic-ui-react';
import { POSButton, POSImage, useSelectedFilterValues } from '@publicisgroupe/p-os-components';
import { v4 as uuidv4 } from 'uuid';

import useFeatureFlags from 'components/FeatureFlagsProvider/hooks/useFeatureFlags';
import useErrorNotification from 'hooks/useErrorNotification';
import { usePageInfoData } from './hooks/usePageInfoData';
import { useHelpSectionPageInfo } from './hooks/useHelpSectionPageInfo';
import NoData from '../NoData';

import { ImageContainer, PageInfoDto } from 'types/PageInfoDto';
import { t } from 'utils/resources';

import './PageInfo.less';

interface PageInfoProps {
  module: string;
  openImageContainerModal: (image: ImageContainer) => void;
}

interface InfoHeaderProps {
  headerLogo: string;
  textBox: string;
}

interface ImageBoxProps {
  image: ImageContainer;
  openImageContainerModal: (image: ImageContainer) => void;
}

interface ProTipsProps {
  protipbox: string;
}

interface InnerHTMLProps {
  innerHTMLString: string;
  classNameString?: string;
}

interface EmailLinkFooterProps {
  selectedClient: string;
}

interface ContactTeamProps {
  learnaboutPGOS: string;
}

const Header = ({ headerLogo, textBox }: InfoHeaderProps) => (
  <div className="header">
    <Grid>
      <Grid.Row columns={2} className="header-content">
        <Grid.Column width={5} className="header-content-logo">
          <POSImage src={headerLogo} data-testid="header-content-logo" />
        </Grid.Column>
        <Grid.Column width={11} className="header-content-text">
          <InnerHTML innerHTMLString={textBox} />
        </Grid.Column>
      </Grid.Row>
    </Grid>
  </div>
);

const ImageBox = ({ image, openImageContainerModal }: ImageBoxProps) => (
  <div
    onClick={() => openImageContainerModal(image)}
    className="image-container-section"
    data-testid="image-container-section"
    key={`image-container-${uuidv4()}`}
    onKeyDown={() => openImageContainerModal(image)}
    role="menuitem"
    tabIndex={0}
  >
    {image.contentImage && image.imageText && (
      <div key={`image-${uuidv4()}`}>
        <POSImage
          src={image.contentImage}
          className="image-container-img"
          data-testid="image-container"
        />
        <InnerHTML innerHTMLString={image.imageText} classNameString={'image-container-caption'} />
      </div>
    )}
  </div>
);

const ProTips = ({ protipbox }: ProTipsProps) => {
  return (
    <div className="pro-tips-section">
      <div className="pro-tips-section-content">
        <p className="pro-tips-section-content-title">{t('HelpSection.PageInfo.ProTips')}</p>
        <InnerHTML
          innerHTMLString={protipbox}
          classNameString={'pro-tips-section-content-description'}
        />
      </div>
    </div>
  );
};

const InnerHTML = ({ innerHTMLString, classNameString = '' }: InnerHTMLProps) => (
  <div
    className={classNameString}
    dangerouslySetInnerHTML={{
      __html: innerHTMLString,
    }}
  />
);

const ContactTeam = ({ learnaboutPGOS }: ContactTeamProps) => (
  <div className="contact-team">
    <div className="content">
      <POSButton inverted className="btn-contact-team">
        {t('HelpSection.PageInfo.ContactTeam')}
      </POSButton>
      <InnerHTML innerHTMLString={learnaboutPGOS} classNameString={'description'} />
      <a className="schedule" href="#">
        {t('HelpSection.PageInfo.ScheduleMeeting')}
      </a>
    </div>
  </div>
);

const prepareEmailSubject = (selectedClient: string) => {
  if (!selectedClient) return t('BugReportMail.Subject');

  return `${selectedClient} - ${t('BugReportMail.Subject')}`;
};

const EmailLinkFooter = ({ selectedClient }: EmailLinkFooterProps) => {
  return (
    <div className="tab-footer">
      <div className="content">
        <p className="header">{t('BugReportModal.Title')}</p>
        <p>{t('BugReportModal.Description')}</p>
        <a
          className="card-action custom-button"
          href={`mailto:${t('BugReportMail.Email')}?subject=${encodeURIComponent(
            prepareEmailSubject(selectedClient)
          )}&body=${encodeURIComponent(t('BugReportMail.Body'))}`}
        >
          {t('BugReportModal.LinkText')}
        </a>
      </div>
    </div>
  );
};

const PageInfo = ({ module, openImageContainerModal }: PageInfoProps) => {
  const { client: selectedClient } = useSelectedFilterValues();
  const { featureFlags, isLoading } = useFeatureFlags();
  const location = useLocation();
  const { showErrorNotification } = useErrorNotification();

  const configApiRequest = usePageInfoData();
  const pageInfoRequest = useHelpSectionPageInfo();

  const [helpSection, setHelpSection] = useState<PageInfoDto[]>();
  const [pageInfoData, setPageInfoData] = useState<PageInfoDto>();

  const { UseConfigModule: useConfigModule } = featureFlags;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const helpSectionData = await configApiRequest(useConfigModule);

        setHelpSection(helpSectionData);
      } catch (error) {
        showErrorNotification({
          errorTitle: t('HelpSectionErrorTitle'),
          content: t('PageInfoErrorMessage'),
        });
      }
    };

    fetchData();
  }, [configApiRequest, useConfigModule, showErrorNotification]);

  useEffect(() => {
    if (helpSection && helpSection.length > 0) {
      const pageInfo = pageInfoRequest(module, helpSection, location);

      setPageInfoData(pageInfo);
    }
  }, [pageInfoRequest, module, helpSection, location]);

  if (isLoading || !pageInfoData) return <NoData />;

  return (
    <>
      <Tab.Pane>
        <div className="tabpane-content">
          <div className="page-info-container">
            {pageInfoData.headerLogo && pageInfoData.textBox && (
              <Header headerLogo={pageInfoData.headerLogo} textBox={pageInfoData.textBox} />
            )}

            {pageInfoData.imageContainer && pageInfoData.imageContainer.length > 0 && (
              <div className="image-container">
                {pageInfoData.imageContainer.map((image: ImageContainer) => (
                  <ImageBox
                    key={`image-text-${uuidv4()}`}
                    image={image}
                    openImageContainerModal={openImageContainerModal}
                  />
                ))}
              </div>
            )}

            {pageInfoData.protipbox && <ProTips protipbox={pageInfoData.protipbox} />}

            {pageInfoData.freetextbox && (
              <InnerHTML
                innerHTMLString={pageInfoData.freetextbox}
                classNameString={'free-textbox-content'}
              />
            )}

            {pageInfoData.learnaboutPGOS && (
              <ContactTeam learnaboutPGOS={pageInfoData.learnaboutPGOS} />
            )}
          </div>
        </div>
      </Tab.Pane>

      <EmailLinkFooter selectedClient={selectedClient} />
    </>
  );
};

export default PageInfo;
